import { useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';

/**
 * Performance Monitoring Component
 * Tracks and reports performance metrics
 */

interface PerformanceMetrics {
  renderTime: number;
  loadTime: number;
  domContentLoaded: number;
  firstContentfulPaint: number;
  memoryUsage?: number;
}

export function PerformanceMonitor() {
  const reportedRef = useRef(false);

  useEffect(() => {
    if (reportedRef.current) return;
    reportedRef.current = true;

    // Wait for page to fully load
    if (document.readyState === 'complete') {
      reportMetrics();
    } else {
      window.addEventListener('load', reportMetrics);
      return () => window.removeEventListener('load', reportMetrics);
    }
  }, []);

  const reportMetrics = async () => {
    const perfEntries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
    
    if (perfEntries.length === 0) return;

    const timing = perfEntries[0];
    const metrics: PerformanceMetrics = {
      renderTime: timing.domComplete - timing.domInteractive,
      loadTime: timing.loadEventEnd - timing.fetchStart,
      domContentLoaded: timing.domContentLoadedEventEnd - timing.fetchStart,
      firstContentfulPaint: getFirstContentfulPaint(),
      memoryUsage: getMemoryUsage(),
    };

    // Log to console in development
    if (import.meta.env.DEV) {
      console.table({
        'Render Time': `${metrics.renderTime.toFixed(0)}ms`,
        'Load Time': `${metrics.loadTime.toFixed(0)}ms`,
        'DOM Content Loaded': `${metrics.domContentLoaded.toFixed(0)}ms`,
        'First Contentful Paint': `${metrics.firstContentfulPaint.toFixed(0)}ms`,
        'Memory Usage': metrics.memoryUsage ? `${(metrics.memoryUsage / 1048576).toFixed(2)}MB` : 'N/A',
      });
    }

    // Send to tracking service
    try {
      const sessionId = (() => { try { return sessionStorage.getItem('obha_session_id'); } catch (err) { console.error('[PerformanceMonitor]', err); return null; } })();
      if (sessionId) {
        await supabase.functions.invoke('track-event', {
          body: {
            sessionId,
            eventType: 'performance',
            eventData: metrics,
          },
        });
      }
    } catch (error) {
      console.error('[PerformanceMonitor]', 'Failed to report metrics', error);
    }
  };

  return null;
}

function getFirstContentfulPaint(): number {
  const paintEntries = performance.getEntriesByType('paint');
  const fcp = paintEntries.find(entry => entry.name === 'first-contentful-paint');
  return fcp ? fcp.startTime : 0;
}

function getMemoryUsage(): number | undefined {
  // @ts-expect-error performance.memory is Chrome-only
  if (performance.memory) {
    // @ts-expect-error performance.memory is Chrome-only
    return performance.memory.usedJSHeapSize;
  }
  return undefined;
}

/**
 * Web Vitals tracking
 */
export function trackWebVitals() {
  if (typeof window === 'undefined') return;

  // Track Largest Contentful Paint (LCP)
  new PerformanceObserver((list) => {
    const entries = list.getEntries();
    const lastEntry = entries[entries.length - 1];
    
    trackMetric('lcp', lastEntry.startTime);
  }).observe({ entryTypes: ['largest-contentful-paint'] });

  // Track First Input Delay (FID)
  new PerformanceObserver((list) => {
    const entries = list.getEntries();
    entries.forEach(entry => {
      // @ts-expect-error PerformanceEntry processingStart is not in standard types
      trackMetric('fid', entry.processingStart - entry.startTime);
    });
  }).observe({ entryTypes: ['first-input'] });

  // Track Cumulative Layout Shift (CLS)
  let clsValue = 0;
  new PerformanceObserver((list) => {
    for (const entry of list.getEntries()) {
      // @ts-expect-error LayoutShift hadRecentInput is not in standard types
      if (!entry.hadRecentInput) {
        // @ts-expect-error LayoutShift value
        clsValue += entry.value;
      }
    }
    trackMetric('cls', clsValue);
  }).observe({ entryTypes: ['layout-shift'] });
}

function trackMetric(name: string, value: number) {
  if (import.meta.env.DEV) {
    console.log(`[Web Vital] ${name.toUpperCase()}:`, value.toFixed(2));
  }
  
  // Could send to analytics service
}
